import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { getContactPersonsForDebitor } from '../../api/getContactPersonsForDebitor.js';
import { ContactPerson } from '../../db/ContactPerson.js';
import type { ExtractedCompanyInfo } from '../../features/information-extraction/db/Company.js';
import {
  ExtractedProductInfo,
  ManualCorrectedProductInfo,
} from '../../features/information-extraction/db/ExtractedProductInfo.js';
import { describeFieldChanges } from '../../features/information-extraction/db/field-changes.js';
import { MailProcessing, type AbstractMailExtraction } from '../../features/information-extraction/db/MailExtractionResult.js';
import { DELIVERY_FALLBACK_LABELS, formatDeliveryDay } from './delivery-date-labels.js';
import { validateExtractionForDisplay } from '../../features/export/sap-validation.js';
import { CompanyDebitorPicker } from './CompanyDebitorPicker.js';
import { CorrectionsCard } from './CorrectionsCard.js';
import { ProductEditRow } from './ProductEditRow.js';

interface PersonField {
  icon: string;
  value: Maybe<string>;
  label?: string;
}

// The contact person rendered as its own tile inside a company card: person icon, name,
// partner ID, and every detail we have (e-mail, phone, role, sales office) plus the
// fallback warning when the partner had to be guessed.
const renderContactPersonTile = (company: ExtractedCompanyInfo, contactName: string | null, personFields: PersonField[]) => html`
  <div class="mt-3 pt-3 border-t border-base-300/60">
    <div class="flex items-center justify-between gap-2 mb-2">
      <div class="flex items-center gap-2">
        <x-icon class="text-sm text-base-content/50">person</x-icon>
        <span class="text-xs uppercase tracking-wide font-semibold text-base-content/50">Ansprechpartner</span>
      </div>
      <span class="badge badge-sm font-mono badge-outline" title="Partner-ID (Ansprechpartner)">
        AP ${company.partnerID || '–'}
      </span>
    </div>
    <div class="font-semibold text-base-content">${contactName || 'Name unbekannt'}</div>
    ${personFields.length > 0
      ? html`<div class="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-base-content/60">
          ${personFields.map(
            f => html`<div class="flex items-center gap-1">
              <x-icon class="text-sm">${f.icon}</x-icon>
              ${f.label ? html`<span class="text-base-content/40">${f.label}:</span>` : nothing} ${f.value}
            </div>`,
          )}
        </div>`
      : nothing}
    ${company.partnerIDFallback
      ? html`<div class="flex items-start gap-1 mt-2 text-xs text-warning">
          <x-icon class="text-sm shrink-0">warning</x-icon>
          <span>Ansprechpartner konnte nicht zugeordnet werden – bitte auswählen.</span>
        </div>`
      : nothing}
  </div>
`;

export const MailExtractionResultView = stateful((props: { mailProcessing: AbstractMailExtraction; onChange?: () => void }) => {
  const { mailProcessing, onChange } = props;
  const { extractionResult } = mailProcessing;
  if (!extractionResult) return nothing;

  // The SAP customer number lives only on the live MailProcessing (the mail pipeline),
  // not on frozen TestSetEntry snapshots. Gate the SAP field on the concrete type so this
  // view stays usable for any AbstractMailExtraction.
  const liveProcessing = mailProcessing instanceof MailProcessing ? mailProcessing : null;

  // Live validation drives the red highlighting below. Uses the display variant so
  // extraction-completeness issues (missing dimensions/material) are highlighted too,
  // not just the strict SAP send-gates.
  const report = validateExtractionForDisplay(extractionResult);
  const headerErrorFields = new Set(report.errors.filter(e => e.scope === 'header').map(e => e.field));
  const errorFieldsByProduct = new Map<string, Set<string>>();
  for (const issue of report.errors) {
    if (issue.scope === 'position' && issue.productId) {
      const set = errorFieldsByProduct.get(issue.productId) ?? new Set<string>();
      set.add(issue.field);
      errorFieldsByProduct.set(issue.productId, set);
    }
  }
  const productHasError = (p: ExtractedProductInfo) => (errorFieldsByProduct.get(p._id)?.size ?? 0) > 0;
  const fieldError = (p: ExtractedProductInfo, field: string) => errorFieldsByProduct.get(p._id)?.has(field) ?? false;
  const cellError = 'text-error font-semibold';

  const [editingProductId, setEditingProductId] = useState<Maybe<string>>(null);
  const [editingCompanyKey, setEditingCompanyKey] = useState<Maybe<string>>(null);
  const [version, setVersion] = useState(0);

  // SAP customer number lives on the processing (not the company) but belongs visually in the
  // requesting-company card per the redesign — it's the master-data anchor for that firm.
  const [isEditingSapNumber, setIsEditingSapNumber] = useState(false);
  const [editedSapNumber, setEditedSapNumber] = useState('');

  const renderSapNumberField = (proc: MailProcessing) => {
    const displayValue = proc.sapNumber || '';
    const handleSave = async () => {
      proc.sapNumber = editedSapNumber;
      await proc.save();
      setIsEditingSapNumber(false);
      onChange?.();
    };
    return isEditingSapNumber
      ? html`
          <div class="flex items-center gap-1">
            <input
              type="text"
              class="input input-bordered input-xs w-32 font-mono tabular-nums"
              .value=${editedSapNumber}
              @input=${(e: Event) => setEditedSapNumber((e.target as HTMLInputElement).value)}
              @keypress=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') handleSave();
              }}
              placeholder="SAP-Nr."
            />
            <button class="btn btn-ghost btn-xs btn-square" @click=${handleSave}>
              <x-icon class="text-sm">check</x-icon>
            </button>
            <button class="btn btn-ghost btn-xs btn-square" @click=${() => setIsEditingSapNumber(false)}>
              <x-icon class="text-sm">close</x-icon>
            </button>
          </div>
        `
      : html`
          <div class="flex items-center gap-2 rounded-lg bg-base-200/70 px-2.5 py-1.5">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-base-content/40">SAP</span>
            ${displayValue
              ? html`<span class="text-sm font-semibold font-mono tabular-nums">${displayValue}</span>`
              : html`<span class="text-sm text-base-content/40 italic">–</span>`}
            <button
              class="text-base-content/40 hover:text-base-content/70"
              title="SAP-Nummer bearbeiten"
              @click=${() => {
                setEditedSapNumber(displayValue);
                setIsEditingSapNumber(true);
              }}
            >
              <x-icon class="text-sm">edit</x-icon>
            </button>
          </div>
        `;
  };

  // Load the master contact records for the company so the contact-person tile can show
  // everything we know about the resolved partner (e-mail, role, sales office) — the
  // extraction alone often only carries the name and a phone number. The per-company lookup
  // happens inside renderCompany by partner ID.
  const requestingDebitor = extractionResult.requestingCompany?.debitor ?? null;
  const contactSelection = { _id: 1, partner: 1, firstName: 1, lastName: 1, email: 1 } as const;
  const { value: requestingContacts } = useQuery(
    () => (requestingDebitor ? getContactPersonsForDebitor(requestingDebitor)(contactSelection) : Promise.resolve([])),
    [requestingDebitor],
  );
  const contactsByDebitor = new Map<string, ContactPerson[]>();
  if (requestingDebitor && requestingContacts) contactsByDebitor.set(requestingDebitor, requestingContacts);

  const renderCompany = (company: Maybe<ExtractedCompanyInfo>, companyKey: string, icon: string, label: string) => {
    if (!company)
      return html`<div class="card border border-dashed border-base-300 rounded-lg">
        <div class="card-body flex flex-col items-center text-center py-8 text-base-content/40">
          <x-icon class="text-3xl mb-2">business_center</x-icon>
          <span>Keine Firmeninformationen gefunden</span>
        </div>
      </div>`;

    const isEditing = editingCompanyKey === companyKey;

    // Only the requesting company feeds the SAP header (AG_PARTN / NAME).
    const isRequesting = companyKey === 'requesting';
    const debitorMissing = isRequesting && headerErrorFields.has('debitor');
    const nameMissing = isRequesting && headerErrorFields.has('name');

    // Contact person: shown as its own tile when we have any person info. Enrich the
    // extracted signals with the resolved master record (matched by partner ID) so the tile
    // shows as much as we know — e-mail, role and sales office, not just name + phone.
    const masterContact =
      company.debitor && company.partnerID
        ? ((contactsByDebitor.get(company.debitor) ?? []).find(c => c.partner === company.partnerID) ?? null)
        : null;
    const masterName = masterContact
      ? [masterContact.firstName, masterContact.lastName].filter(Boolean).join(' ') || null
      : null;
    const contactName = masterName || company.partnerName || company.contactPersonName || null;
    const hasContactPerson = !!(contactName || company.partnerID);

    // Person details from master data + the e-mail signature. The master e-mail wins
    // (canonical); phone exists only on the extracted signature.
    const personFields: PersonField[] = [
      { icon: 'email', value: masterContact?.email || company.email },
      { icon: 'phone', value: company.phone },
    ];
    const visiblePersonFields = personFields.filter(f => f.value && f.value !== '-');

    // Company-level row: e-mail/phone move into the person tile when one is shown; otherwise
    // keep them here so a billing company without a named contact doesn't lose them.
    const contactFields = [
      ...(hasContactPerson
        ? []
        : [
            { icon: 'email', value: company.email },
            { icon: 'phone', value: company.phone },
          ]),
      { icon: 'badge', value: company.taxId },
    ].filter(f => f.value && f.value !== '-');

    return html`
      <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm">
        <div class="card-body p-6">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <x-icon class="text-sm text-base-content/40">${icon}</x-icon>
                <h3 class="text-[10.5px] uppercase tracking-wider font-semibold text-base-content/40">${label}</h3>
              </div>
              <h3 class="text-2xl font-bold tracking-tight leading-tight ${nameMissing ? 'text-error' : 'text-base-content'}">
                ${company.name || (nameMissing ? 'Firmenname fehlt' : '')}
              </h3>
              <div class="text-base-content/60 text-sm mt-1">
                ${company.address || ''} ${company.zipCode ? `, ${company.zipCode}` : ''} ${company.city || ''}
              </div>
            </div>
            <div class="flex flex-col items-end gap-2 shrink-0">
              ${isRequesting && liveProcessing ? renderSapNumberField(liveProcessing) : nothing}
              <div class="flex items-center gap-2">
                <span
                  class="badge badge-sm font-mono ${debitorMissing ? 'badge-error' : 'badge-outline'}"
                  title=${debitorMissing ? 'Debitor (AG_PARTN) fehlt – Pflichtfeld' : 'Debitor'}
                  >${company.debitor || '–'}</span
                >
                <button
                  class="btn btn-ghost btn-xs"
                  @click=${() => setEditingCompanyKey(isEditing ? null : companyKey)}
                  title="Debitor / Ansprechpartner ändern"
                >
                  <x-icon class="text-xs">${isEditing ? 'expand_less' : 'edit'}</x-icon>
                </button>
              </div>
            </div>
          </div>

          ${contactFields.length > 0
            ? html`
                <div class="flex flex-wrap gap-3 mt-2 text-sm text-base-content/60">
                  ${contactFields.map(
                    f => html`<div class="flex items-center gap-1"><x-icon class="text-sm">${f.icon}</x-icon>${f.value}</div>`,
                  )}
                </div>
              `
            : nothing}

          ${hasContactPerson ? renderContactPersonTile(company, contactName, visiblePersonFields) : nothing}

          ${CompanyDebitorPicker({
            company,
            mailProcessing,
            isEditing,
            onClose: () => {
              setEditingCompanyKey(null);
              setVersion(version + 1);
              onChange?.();
            },
          })}
        </div>
      </div>
    `;
  };

  const renderAlert = (text: string) => html`
    <div
      class="flex items-center gap-2 bg-warning/10 border border-warning/20 rounded-lg px-4 py-3 text-base mb-8 max-w-2xl mx-auto"
    >
      <x-icon class="text-warning text-lg">warning</x-icon>
      <span class="text-warning font-medium">${text}</span>
    </div>
  `;

  const correctedProducts = extractionResult.correctedProducts ?? [];
  const extractedProducts = (extractionResult.extractedProducts ?? []).filter(extracted => {
    const isCorrected =
      correctedProducts.findIndex(corrected => corrected.extractedProductInfoId === extracted._id) !== -1;
    if (isCorrected) return false;
    return true;
  });

  const unsortedProducts = [...correctedProducts, ...extractedProducts];
  const removedProducts = unsortedProducts.filter(p => mailProcessing.isProductRemoved(p));
  const activeProducts = unsortedProducts.filter(p => !mailProcessing.isProductRemoved(p));

  const mainProducts = activeProducts.filter(p => !p.isAlternative);
  const alternativeProducts = activeProducts.filter(p => p.isAlternative);
  const products: ExtractedProductInfo[] = [];
  for (const main of mainProducts) {
    products.push(main);
    const alts = alternativeProducts.filter(a => a.alternativeFor === main.positionNumber);
    products.push(...alts);
  }
  const orphanAlts = alternativeProducts.filter(
    a => !mainProducts.some(m => m.positionNumber === a.alternativeFor),
  );
  products.push(...orphanAlts);

  const getRowClass = (product: ExtractedProductInfo) => {
    if (productHasError(product)) return 'bg-error/10';
    if (product instanceof ManualCorrectedProductInfo) {
      if (!product.extractedProductInfoId) return 'bg-info/10';
      if (mailProcessing.isProductCorrected(product)) return 'bg-success/10';
    }
    return '';
  };

  const getStatusBadge = (product: ExtractedProductInfo) => {
    const status = mailProcessing.getProductStatus(product);
    if (status === 'Neu erstellt')
      return html`<span class="badge badge-info badge-sm gap-1"><x-icon class="text-xs">add_circle</x-icon>${status}</span>`;
    if (status === 'Korrigiert') {
      const changes = mailProcessing.getProductChanges(product);
      const tip = describeFieldChanges(changes) || 'Korrigiert';
      return html`<span class="badge badge-success badge-sm gap-1" title=${tip}
        ><x-icon class="text-xs">edit</x-icon>${status}</span
      >`;
    }
    return html`<span class="badge badge-ghost badge-sm gap-1"><x-icon class="text-xs">smart_toy</x-icon>${status}</span>`;
  };

  const handleNewProduct = async () => {
    const newId = await mailProcessing.createProduct();
    setEditingProductId(newId);
    onChange?.();
  };

  const handleRemoveProduct = async (product: ExtractedProductInfo) => {
    if (editingProductId === product._id) setEditingProductId(null);
    await mailProcessing.removeProduct(product);
    setVersion(version + 1);
    onChange?.();
  };

  const handleRestoreProduct = async (product: ExtractedProductInfo) => {
    await mailProcessing.restoreProduct(product);
    setVersion(version + 1);
    onChange?.();
  };

  const requesting = extractionResult.requestingCompany;

  // Wunschlieferdatum aus dem Routenfahrplan. Sichtbar, weil es sonst erst im XML auftaucht —
  // und weil ein Termin, den niemand nachvollziehen kann, im Zweifel von Hand überschrieben
  // wird. Deshalb steht die Herleitung daneben.
  const renderDeliveryDate = () => {
    const basis = extractionResult.deliveryDateBasis;
    const planned = extractionResult.plannedDeliveryDate;
    const manual = extractionResult.manualDeliveryDate;
    const effective = manual ?? planned;
    const customerWish = extractionResult.desiredDeliveryDate?.toFormat('dd.MM.yyyy') ?? null;

    const setManual = async (value: string | null) => {
      extractionResult.manualDeliveryDate = value || null;
      await mailProcessing.save();
      setVersion(version + 1);
      onChange?.();
    };

    const derivation = manual
      ? 'Von Hand gesetzt'
      : basis?.source === 'abholer'
        ? `Selbstabholer · Zone ${basis.transportZone ?? '–'}`
        : basis?.source === 'fahrplan'
          ? [
              `Zone ${basis.transportZone ?? '–'}`,
              basis.departureDate ? `Versand ${formatDeliveryDay(basis.departureDate)}` : null,
              basis.transitDays ? `Transportzeit ${basis.transitDays * 24} h` : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : basis?.source === 'ersatzregel'
            ? `${DELIVERY_FALLBACK_LABELS[basis.fallbackReason ?? ''] ?? 'Kein Routenfahrplan'} · Verarbeitung + 3 Werktage`
            : 'Noch nicht berechnet';

    return html`
      <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm">
        <div class="card-body p-6">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div class="min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <x-icon class="text-sm text-base-content/40">local_shipping</x-icon>
                <h3 class="text-[10.5px] uppercase tracking-wider font-semibold text-base-content/40">
                  Liefertermin
                </h3>
              </div>
              <h3 class="text-2xl font-bold tracking-tight leading-tight text-base-content">
                ${effective ? formatDeliveryDay(effective) : 'Noch nicht berechnet'}
              </h3>
              <div class="text-base-content/60 text-sm mt-1">${derivation}</div>
              ${manual && planned
                ? html`<div class="text-base-content/40 text-sm mt-0.5">
                    Laut Routenfahrplan: ${formatDeliveryDay(planned)}
                  </div>`
                : nothing}
              ${customerWish
                ? html`<div class="text-base-content/40 text-sm mt-0.5">Laut Anfrage: ${customerWish}</div>`
                : nothing}
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <input
                type="date"
                class="input input-bordered input-sm"
                .value=${manual ?? ''}
                title="Liefertermin von Hand festlegen"
                @change=${(e: Event) => setManual((e.target as HTMLInputElement).value)}
              />
              ${manual
                ? html`<button
                    class="btn btn-ghost btn-xs"
                    title="Handeingabe verwerfen"
                    @click=${() => setManual(null)}
                  >
                    <x-icon class="text-xs">undo</x-icon>
                  </button>`
                : nothing}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  return html`
    <div class="w-full">
      ${extractionResult.notAnOrderDocument ? renderAlert('Dieses Dokument ist keine Bestellung') : null}

      <div class="w-full space-y-6">
        <!-- Was von Hand geändert wurde — Firma, Ansprechpartner und Positionen, an einer Stelle -->
        ${CorrectionsCard(mailProcessing)}

        <!-- Company Section -->
        <div class="grid grid-cols-1 gap-4">
          ${renderCompany(requesting, 'requesting', 'business', 'Anfragende Firma')} ${renderDeliveryDate()}
        </div>
      </div>

      <main class="w-full rounded-2xl bg-base-100 border border-base-200 shadow-sm overflow-hidden mt-6">
        <div class="flex items-center gap-3 justify-between px-5 py-4 border-b border-base-200">
          <div class="flex items-center gap-2.5">
            <x-icon class="text-base-content/70">inventory_2</x-icon>
            <h2 class="text-base font-bold tracking-tight text-base-content">Produkte</h2>
            <span class="badge badge-neutral badge-sm">${products.length}</span>
          </div>
          <button class="btn btn-primary btn-sm gap-1" @click=${handleNewProduct}>
            <x-icon class="text-sm">add</x-icon> Neues Produkt
          </button>
        </div>

        <div class="overflow-x-auto">
          <table class="table table-sm text-sm min-w-max">
            <thead>
              <tr class="bg-base-200 text-base-content/70 text-xs uppercase tracking-wide">
                <th class="font-semibold px-2 py-2 w-8"></th>
                <th class="font-semibold px-2 py-2 text-right">Pos.</th>
                <th class="font-semibold px-2 py-2">Status</th>
                <th class="font-semibold px-2 py-2">Gruppe</th>
                <th class="font-semibold px-2 py-2">Material</th>
                <th class="font-semibold px-2 py-2">Lief.</th>
                <th class="font-semibold px-2 py-2 text-right">AD 1 (mm)</th>
                <th class="font-semibold px-2 py-2 text-right">AD 2 (mm)</th>
                <th class="font-semibold px-2 py-2 text-right">Wand (mm)</th>
                <th class="font-semibold px-2 py-2 text-right">ID (mm)</th>
                <th class="font-semibold px-2 py-2 text-right">Menge</th>
                <th class="font-semibold px-2 py-2 text-right">Laenge (mm)</th>
                <th class="font-semibold px-2 py-2">Laengenart</th>
                <th class="font-semibold px-2 py-2">Mat-Nr</th>
              </tr>
            </thead>
            <tbody>
              ${products.length === 0
                ? html`<tr>
                    <td colspan="14" class="text-center text-base-content/50 py-8">Keine Produkte vorhanden</td>
                  </tr>`
                : products.map((product: ExtractedProductInfo) => {
                    const isEditing = editingProductId === product._id;
                    const isAlt = !!product.isAlternative;
                    return html`
                      <tr
                        class="${getRowClass(product)} ${isAlt ? 'border-l-2 border-l-base-300 bg-base-200/60' : ''} hover:bg-base-200 transition cursor-pointer"
                        @click=${() => {
                          if (!isEditing) setEditingProductId(product._id);
                        }}
                      >
                        <td class="px-2 py-2">
                          ${editingProductId != null
                            ? nothing
                            : html`<div class="flex items-center gap-1">
                                <button class="btn btn-ghost btn-xs" title="Bearbeiten">
                                  <x-icon class="text-xs">edit</x-icon>
                                </button>
                                <button
                                  class="btn btn-ghost btn-xs text-error"
                                  title="Position entfernen"
                                  @click=${(e: Event) => {
                                    e.stopPropagation();
                                    handleRemoveProduct(product);
                                  }}
                                >
                                  <x-icon class="text-xs">delete</x-icon>
                                </button>
                              </div>`}
                        </td>
                        <td class="px-2 py-2 text-right tabular-nums whitespace-nowrap text-base-content/70">
                          ${product.positionNumber || '–'}
                        </td>
                        <td class="px-2 py-2 text-center">
                          <div class="flex flex-col items-center gap-1">
                            ${getStatusBadge(product)}
                            ${isAlt
                              ? html`<span class="badge badge-warning badge-xs" title="Alternative für Pos. ${product.alternativeFor ?? '?'}"
                                  >ALT${product.alternativeFor ? ` → ${product.alternativeFor}` : ''}</span
                                >`
                              : nothing}
                          </div>
                        </td>
                        <td class="px-2 py-2 whitespace-nowrap">${product.productGroup || '–'}</td>
                        <td
                          class="px-2 py-2 max-w-70 truncate ${fieldError(product, 'material') ? cellError : ''}"
                          title=${product.material || ''}
                        >
                          ${product.material || '–'}
                        </td>
                        <td class="px-2 py-2 whitespace-nowrap">${product.deliveryCondition || '–'}</td>
                        <td
                          class="px-2 py-2 whitespace-nowrap text-right font-mono tabular-nums ${fieldError(product, 'outerDiameter1') ? cellError : ''}"
                        >
                          ${product.outerDiameter1 != null ? product.outerDiameter1 : '–'}
                        </td>
                        <td
                          class="px-2 py-2 whitespace-nowrap text-right font-mono tabular-nums ${fieldError(product, 'outerDiameter1') ? cellError : ''}"
                        >
                          ${product.outerDiameter2 != null ? product.outerDiameter2 : '–'}
                        </td>
                        <td class="px-2 py-2 whitespace-nowrap text-right font-mono tabular-nums">
                          ${product.wallThickness != null ? product.wallThickness : '–'}
                        </td>
                        <td class="px-2 py-2 whitespace-nowrap text-right font-mono tabular-nums">
                          ${product.innerDiameter != null ? product.innerDiameter : '–'}
                        </td>
                        <td
                          class="px-2 py-2 whitespace-nowrap text-right font-mono tabular-nums ${fieldError(product, 'quantity') ? cellError : ''}"
                        >
                          ${product.quantity != null ? product.quantity : '–'}
                        </td>
                        <td
                          class="px-2 py-2 whitespace-nowrap text-right font-mono tabular-nums ${fieldError(product, 'length') ? cellError : ''}"
                        >
                          ${product.length != null ? product.length : '–'}
                        </td>
                        <td class="px-2 py-2 whitespace-nowrap ${fieldError(product, 'lengthType') ? cellError : ''}">
                          ${product.lengthType || '–'}
                        </td>
                        <td class="px-2 py-2 whitespace-nowrap font-mono ${fieldError(product, 'productNumber') ? cellError : ''}">
                          ${product.productNumber || '–'}
                        </td>
                      </tr>
                      ${isEditing
                        ? html`<tr>
                            <td colspan="14" class="p-0">
                              ${ProductEditRow({
                                product,
                                mailProcessing,
                                onSave: () => {
                                  setEditingProductId(null);
                                  setVersion(version + 1);
                                  onChange?.();
                                },
                                onClose: () => setEditingProductId(null),
                              })}
                            </td>
                          </tr>`
                        : nothing}
                    `;
                  })}
            </tbody>
          </table>
        </div>
      </main>

      ${removedProducts.length > 0
        ? html`<section class="w-full rounded-2xl bg-base-100 border border-base-200 shadow-sm overflow-hidden mt-6">
            <div class="flex items-center gap-2.5 px-5 py-4 border-b border-base-200">
              <x-icon class="text-base-content/40">delete</x-icon>
              <h2 class="text-base font-bold tracking-tight text-base-content/60">Entfernte Positionen</h2>
              <span class="badge badge-neutral badge-sm">${removedProducts.length}</span>
              <span class="text-xs text-base-content/50">– werden nicht an SAP übermittelt</span>
            </div>
            <ul class="divide-y divide-base-200">
              ${removedProducts.map(
                p => html`<li class="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                  <div class="flex items-center gap-3 min-w-0 line-through text-base-content/60">
                    <span class="font-mono">${p.positionNumber || '–'}</span>
                    <span class="truncate">${p.material || '–'}</span>
                    <span class="font-mono">${p.productNumber || '–'}</span>
                    ${p.quantity != null ? html`<span>×${p.quantity}</span>` : nothing}
                  </div>
                  <button class="btn btn-ghost btn-xs gap-1" title="Wiederherstellen" @click=${() => handleRestoreProduct(p)}>
                    <x-icon class="text-xs">undo</x-icon> Wiederherstellen
                  </button>
                </li>`,
              )}
            </ul>
          </section>`
        : nothing}
    </div>
  `;
});
